import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach Bearer token from localStorage for defense in depth (cookies also sent)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default api;

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export const DOC_TYPE_LABELS = {
  awb_form: "AWB Form",
  customs_form: "Customs Form",
  invoice: "Invoice",
  permit: "Permit",
  terminal_charges: "Terminal Charges",
  other: "Other",
};

// Auto-retry a network call on transient errors (5xx or no-response).
// Does NOT retry 4xx (client errors). Default: 1 retry (2 total attempts) with 400ms then 800ms backoff.
// Backoff is jittered ±20% by default to avoid thundering-herd against a busy server when many files retry at once.
export async function withAutoRetry(fn, opts = {}) {
  const { maxAttempts = 2, baseDelay = 400, onRetry, onRecovered, jitter = 0.2 } = opts;
  let lastErr;
  let hadFailure = false;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const result = await fn();
      if (hadFailure && onRecovered) {
        try { await onRecovered({ attempts: i + 1 }); } catch {}
      }
      return result;
    }
    catch (e) {
      lastErr = e;
      hadFailure = true;
      const status = e?.response?.status;
      const isTransient = !status || status >= 500 || e.code === "ECONNABORTED" || e.message === "Network Error";
      if (!isTransient || i === maxAttempts - 1) throw e;
      const base = baseDelay * Math.pow(2, i);
      const j = jitter > 0 ? 1 - jitter + Math.random() * (2 * jitter) : 1; // e.g. 0.8..1.2
      const delay = Math.round(base * j);
      if (onRetry) onRetry({ attempt: i + 1, nextIn: delay, error: e });
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

export const REQUIRED_DOC_TYPES = ["awb_form", "invoice", "permit", "terminal_charges", "customs_form"];

export const DOC_TYPE_COLORS = {
  awb_form: "bg-blue-100 text-blue-900 border-blue-200",
  customs_form: "bg-amber-100 text-amber-900 border-amber-200",
  invoice: "bg-emerald-100 text-emerald-900 border-emerald-200",
  permit: "bg-purple-100 text-purple-900 border-purple-200",
  terminal_charges: "bg-orange-100 text-orange-900 border-orange-200",
  other: "bg-zinc-100 text-zinc-900 border-zinc-200",
};
